import { sqliteTable, text, real, integer, alias } from "drizzle-orm/sqlite-core";
import { eq, desc, or, and } from "drizzle-orm";
import { Token } from "./tokens";
import { getDb } from "../db";
import { LPToken } from "./lpToken";

export type SelectPair = typeof Pair.$inferSelect;
export type InsertPair = typeof Pair.$inferInsert;

export const Pair = sqliteTable('Pair', {
	address: text('pairAddress').primaryKey(),
	dexAddress: text('dexAddress').notNull().default("component_rdx1cqy7sq3mxj2whhlqlryy05hzs96m0ajnv23e7j7vanmdwwlccnmz68"),
	baseToken: text('baseTokenAddress').notNull().references(() => Token.address),
	quoteToken: text('quoteTokenAddress').notNull().default("resource_rdx1t5ywq4c6nd2lxkemkv4uzt8v7x7smjcguzq5sgafwtasa6luq7fclq").references(() => Token.address),
	basePool: text('basePoolAddress').notNull().unique(),
	quotePool: text('quotePoolAddress').notNull().unique(),
	baseLPToken: text('baseLPAddress').notNull().unique().references(() => LPToken.address),
	quoteLPToken: text('quoteLPAddress').notNull().unique().references(() => LPToken.address),
	baseAPY: real('baseAPY'),
	quoteAPY: real('quoteAPY'),
	baseAPY7D: real('baseAPY7D'),
	quoteAPY7D: real('quoteAPY7D'),
	config: text('config', { mode: 'json' }),

	baseVolume24H: real('baseVolume24H'),
	quoteVolume24H: real('quoteVolume24H'),
	bidPrice: real('bidPrice'),
	askPrice: real('askPrice'),
	lastPrice: real('lastPrice'),

	baseTVL: real('baseTVL'),
	quoteTVL: real('quoteTVL'),
	tvlUSD: real('tvlUSD'),

	createdAt: text('createdAt'),
	updatedAt: text('updatedAt'),
	updatedAPYAt: text('updatedAPYAt'),
	stateVersion: integer('stateVersion')
});

export function storePair(pair: InsertPair) {
	pair.updatedAt = new Date().toISOString();

	return getDb()
		.insert(Pair)
		.values(pair)
		.onConflictDoUpdate({ target: Pair.address, set: { ...pair } })
		.returning({ address: Pair.address })
}

export function updatePair(address: string, pair: InsertPair) {
	pair.updatedAt = new Date().toISOString();

	return getDb()
		.update(Pair)
		.set(pair)
		.where(eq(Pair.address, address));
}

export function patchPair(address: string, pair: Partial<InsertPair>) {
	return getDb()
		.update(Pair)
		.set({
			...pair,
			updatedAt: new Date().toISOString()
		})
		.where(eq(Pair.address, address));
}

export async function getPairs() {
	const quoteToken = alias(Token, "quoteToken");

	return getDb()
		.select()
		.from(Pair)
		.innerJoin(Token, eq(Token.address, Pair.baseToken))
		.innerJoin(quoteToken, eq(quoteToken.address, Pair.quoteToken))
		.all();
}

export async function getPair(address: string) {
	return getDb()
		.select()
		.from(Pair)
		.innerJoin(Token, eq(Token.address, Pair.baseToken))
		.where(eq(Pair.address, address))
		.all();
}

export async function getLatestPairs(dexAddress: string) {
	return getDb()
		.select()
		.from(Pair)
		.where(eq(Pair.dexAddress, dexAddress))
		.orderBy(desc(Pair.stateVersion))
		.limit(1)
		.all();
}

export async function getPairByBaseToken(address: string) {
	return getDb()
		.select()
		.from(Pair)
		.innerJoin(Token, eq(Token.address, Pair.baseToken))
		.where(eq(Token.address, address))
		.all();
}

export async function getPairByBaseAndQuoteToken(baseAddress: string, quoteAddress: string) {
	const quoteToken = alias(Token, "quoteToken");

	return getDb()
		.select()
		.from(Pair)
		.innerJoin(Token, eq(Token.address, Pair.baseToken))
		.innerJoin(quoteToken, eq(quoteToken.address, Pair.quoteToken))
		.where(and(
			eq(Pair.baseToken, baseAddress),
			eq(Pair.quoteToken, quoteAddress)
		))
		.all();
}

export async function getPairByBaseOrQuoteToken(address: string) {
	const quoteToken = alias(Token, "quoteToken");

	return getDb()
		.select()
		.from(Pair)
		.innerJoin(Token, eq(Token.address, Pair.baseToken))
		.innerJoin(quoteToken, eq(quoteToken.address, Pair.quoteToken))
		.where(or(
			eq(Pair.baseToken, address),
			eq(Pair.quoteToken, address)
		))
		.all();
}

export async function getPairByLPToken(address: string) {
	return getDb()
		.select()
		.from(Pair)
		.where(or(
			eq(Pair.baseLPToken, address),
			eq(Pair.quoteLPToken, address)
		))
		.all();
}
